import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  DialogActions,
  Grid
} from '@mui/material';
import HelpIcon from './HelpIcon';
import DeleteIcon from '@mui/icons-material/Delete';
import ConditionCard from './ConditionCard';
import { useInitialConfig, mergeInto } from '../utils/useInitialConfig';

// --- Constants for Dropdowns ---
const PLANETS = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
const HOUSES = ['From 7th House', 'With Any Aspect']; // Example values
const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const NAKSHATRAS = ['Ashvini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra', 'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Svati', 'Vishakha', 'Anuradha', 'Jyeshtha', 'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'];
const PLANET_STATES = ['retrograde', 'combust', 'fast', 'superfast', 'slow'];

const COLOR_OPTIONS = [
  { name: 'Red', value: 'rgba(244, 67, 54, 0.5)' },
  { name: 'Pink', value: 'rgba(233, 30, 99, 0.5)' },
  { name: 'Purple', value: 'rgba(156, 39, 176, 0.5)' },
  { name: 'Deep Purple', value: 'rgba(103, 58, 183, 0.5)' },
  { name: 'Indigo', value: 'rgba(63, 81, 181, 0.5)' },
  { name: 'Blue', value: 'rgba(33, 150, 243, 0.5)' },
  { name: 'Light Blue', value: 'rgba(3, 169, 244, 0.5)' },
  { name: 'Cyan', value: 'rgba(0, 188, 212, 0.5)' },
  { name: 'Teal', value: 'rgba(0, 150, 136, 0.5)' },
  { name: 'Green', value: 'rgba(76, 175, 80, 0.5)' },
  { name: 'Light Green', value: 'rgba(139, 195, 74, 0.5)' },
  { name: 'Lime', value: 'rgba(205, 220, 57, 0.5)' },
  { name: 'Yellow', value: 'rgba(255, 235, 59, 0.5)' },
  { name: 'Amber', value: 'rgba(255, 193, 7, 0.5)' },
  { name: 'Orange', value: 'rgba(255, 152, 0, 0.5)' },
  { name: 'Deep Orange', value: 'rgba(255, 87, 34, 0.5)' },
  { name: 'Brown', value: 'rgba(121, 85, 72, 0.5)' },
  { name: 'Grey', value: 'rgba(158, 158, 158, 0.5)' },
  { name: 'Blue Grey', value: 'rgba(96, 125, 139, 0.5)' },
];

/**
 * The top-level prefill keys this dialog's applier reads.
 *
 * <p>⚠ EXPORTED SO AN UNKNOWN FIELD CAN BE REFUSED RATHER THAN SILENTLY DROPPED. A prefill
 * naming a field that does not exist would otherwise open the dialog, change nothing and report
 * success — which for a Copilot is the "a control that lies" failure, in the one place the
 * caller has no way to notice it. Same precedent as `ctsDirection`, which refuses an unknown
 * name with the valid set listed rather than quietly running the default.
 *
 * <p>Nested keys are deliberately NOT listed: `mergeInto` already ignores sub-keys it does not
 * know, and enumerating them here would be a second declaration of every form's shape.
 *
 * <p>`prefillFields.test.js` reads the applier and fails if the two drift apart.
 */
export const PREFILL_FIELDS = Object.freeze([
  'selectedComponents', 'highlightColor', 'scoreThreshold', 'condition1', 'condition2',
  'condition3', 'condition4', 'condition6', 'condition7', 'condition9', 'condition10',
  'condition11',
]);

const Strategy2DesignDialog = ({ open, onClose, onRun, initialConfig = null }) => {
  // State for the first condition row
  const [condition1, setCondition1] = useState({ planet1: 'Jupiter', planet2: 'Mars', house: 'With Any Aspect', value: '1' });
  const [condition2, setCondition2] = useState({ planet1: 'Sun', planet2: 'Venus', sign: 'Leo', value: '1' });
  const [condition3, setCondition3] = useState({ planet1: 'Sun', sign1: 'Aries', planet2: 'Mars', sign2: 'Leo', value: '1' });
  const [condition4, setCondition4] = useState({ planet1: 'Mars', planet2: 'Saturn', nakshatraApart: '8', value: '1' });
  const [condition6, setCondition6] = useState({ planet1: 'Moon', planet2: 'Venus', nakshatra: 'Rohini', value: '1' });
  const [condition7, setCondition7] = useState({ planet1: 'Jupiter', planet2: 'Saturn', signsApart: '5', value: '1' });
  const [condition9, setCondition9] = useState({ planet1: 'Sun', sign: 'Capricorn', value: '1' });
  const [condition10, setCondition10] = useState({ planet1: 'Mars', nakshatra: 'Mula', value: '1' });
  const [condition11, setCondition11] = useState({ planet1: 'Venus', state: 'combust', value: '1' });

  const [selectedComponents, setSelectedComponents] = useState([]);
  const [highlightColor, setHighlightColor] = useState('rgba(244, 67, 54, 0.5)');
  const [scoreThreshold, setScoreThreshold] = useState('2');

  /**
   * PREFILL. `selectedComponents` REPLACES -- the condition list IS the study, so appending to
   * whatever was on screen would run a strategy nobody described. Each condition form merges,
   * so naming one field leaves the rest of that row alone.
   */
  useInitialConfig(initialConfig, (cfg) => {
    if (Array.isArray(cfg.selectedComponents)) setSelectedComponents(cfg.selectedComponents);
    if (cfg.highlightColor !== undefined) setHighlightColor(cfg.highlightColor);
    if (cfg.scoreThreshold !== undefined) setScoreThreshold(String(cfg.scoreThreshold));
    mergeInto(setCondition1, cfg.condition1, ['planet1', 'planet2', 'house', 'value']);
    mergeInto(setCondition2, cfg.condition2, ['planet1', 'planet2', 'sign', 'value']);
    mergeInto(setCondition3, cfg.condition3, ['planet1', 'sign1', 'planet2', 'sign2', 'value']);
    mergeInto(setCondition4, cfg.condition4, ['planet1', 'planet2', 'nakshatraApart', 'value']);
    mergeInto(setCondition6, cfg.condition6, ['planet1', 'planet2', 'nakshatra', 'value']);
    mergeInto(setCondition7, cfg.condition7, ['planet1', 'planet2', 'signsApart', 'value']);
    mergeInto(setCondition9, cfg.condition9, ['planet1', 'sign', 'value']);
    mergeInto(setCondition10, cfg.condition10, ['planet1', 'nakshatra', 'value']);
    mergeInto(setCondition11, cfg.condition11, ['planet1', 'state', 'value']);
  });

  const areAllFieldsFilled = (state, fields) => {
    return fields.every(field => state[field] && state[field] !== '');
  };

  const handleAddComponent = (component) => {
    const isDuplicate = selectedComponents.some(c => JSON.stringify(c) === JSON.stringify(component));
    if (isDuplicate) {
      alert('This exact condition has already been added.');
      return;
    }
    setSelectedComponents(prev => [...prev, component]);
  };

  const handleDeleteComponent = (index) => {
    setSelectedComponents(prev => prev.filter((_, i) => i !== index));
  };

  const handleRun = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      alert('You are not logged in. Please log in again.');
      return;
    }

    try {
      const idToken = await user.getIdToken(true); // Force refresh the token
      console.log('Running strategy with fresh token.');
      onRun({ 
        studies: selectedComponents, 
        color: highlightColor, 
        threshold: scoreThreshold, 
        token: idToken 
      });
    } catch (error) {
      console.error('Error refreshing token:', error);
      alert('Could not refresh authentication token. Please log in again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={false} sx={{ '& .MuiDialog-paper': { width: '33vw' } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{flexGrow: 1, textAlign: 'center', paddingLeft: '48px'}}>Strategy Designer - 2</span>
        <HelpIcon helpKey="strategy2DesignDialog" />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {/* Left Panel: Condition Builders */}
          <Box sx={{ flex: 5, display: 'flex', flexDirection: 'column', gap: 1, border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
            {/* Placeholder for the 10 condition rows */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <ConditionCard>
                  <Grid item xs>
                    <FormControl size="small" fullWidth><Select value={condition1.planet1} onChange={e => setCondition1(p => ({ ...p, planet1: e.target.value }))}>{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl>
                  </Grid>
                  <Grid item>
                    <Typography>aspected by</Typography>
                  </Grid>
                  <Grid item xs>
                    <FormControl size="small" fullWidth><Select value={condition1.planet2} onChange={e => setCondition1(p => ({ ...p, planet2: e.target.value }))}>{PLANETS.filter(p => p !== condition1.planet1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl>
                  </Grid>
                  <Grid item xs>
                    <FormControl size="small" fullWidth><Select value={condition1.house} onChange={e => setCondition1(p => ({ ...p, house: e.target.value }))}>{HOUSES.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}</Select></FormControl>
                  </Grid>
                  <Grid item>
                    <Typography>assign value</Typography>
                  </Grid>
                  <Grid item>
                    <TextField size="small" placeholder="Value" type="number" value={condition1.value} onChange={e => setCondition1(p => ({ ...p, value: e.target.value }))} sx={{ width: '60px' }} />
                  </Grid>
                </ConditionCard>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleAddComponent({ type: 1, ...condition1, summary: `Condition 1: ${condition1.planet1} aspected by ${condition1.planet2} ${condition1.house}` })} disabled={!areAllFieldsFilled(condition1, ['planet1', 'planet2', 'house', 'value']) || condition1.planet1 === condition1.planet2}>ADD</Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <ConditionCard>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition2.planet1} onChange={e => setCondition2(p => ({ ...p, planet1: e.target.value }))}>{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>and</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition2.planet2} onChange={e => setCondition2(p => ({ ...p, planet2: e.target.value }))}>{PLANETS.filter(p => p !== condition2.planet1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>in</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition2.sign} onChange={e => setCondition2(p => ({ ...p, sign: e.target.value }))}>{SIGNS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>assign value</Typography></Grid>
                  <Grid item><TextField size="small" placeholder="Value" type="number" value={condition2.value} onChange={e => setCondition2(p => ({ ...p, value: e.target.value }))} sx={{ width: '60px' }} /></Grid>
                </ConditionCard>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleAddComponent({ type: 2, ...condition2, summary: `Condition 2: ${condition2.planet1} and ${condition2.planet2} in ${condition2.sign}` })} disabled={!areAllFieldsFilled(condition2, ['planet1', 'planet2', 'sign', 'value']) || condition2.planet1 === condition2.planet2}>ADD</Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <ConditionCard>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition3.planet1} onChange={e => setCondition3(p => ({ ...p, planet1: e.target.value }))}>{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>in</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition3.sign1} onChange={e => setCondition3(p => ({ ...p, sign1: e.target.value }))}>{SIGNS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>and</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition3.planet2} onChange={e => setCondition3(p => ({ ...p, planet2: e.target.value }))}>{PLANETS.filter(p => p !== condition3.planet1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>in</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition3.sign2} onChange={e => setCondition3(p => ({ ...p, sign2: e.target.value }))}>{SIGNS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>assign value</Typography></Grid>
                  <Grid item><TextField size="small" placeholder="Value" type="number" value={condition3.value} onChange={e => setCondition3(p => ({ ...p, value: e.target.value }))} sx={{ width: '60px' }} /></Grid>
                </ConditionCard>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleAddComponent({ type: 3, ...condition3, summary: `Condition 3: ${condition3.planet1} in ${condition3.sign1} and ${condition3.planet2} in ${condition3.sign2}` })} disabled={!areAllFieldsFilled(condition3, ['planet1', 'sign1', 'planet2', 'sign2', 'value']) || condition3.planet1 === condition3.planet2}>ADD</Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <ConditionCard>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition4.planet1} onChange={e => setCondition4(p => ({ ...p, planet1: e.target.value }))}>{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>and</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition4.planet2} onChange={e => setCondition4(p => ({ ...p, planet2: e.target.value }))}>{PLANETS.filter(p => p !== condition4.planet1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><TextField size="small" type="number" value={condition4.nakshatraApart} onChange={e => { const v = e.target.value; if (v === '' || (Number(v) >= 0 && Number(v) <= 26)) setCondition4(p => ({ ...p, nakshatraApart: v })); }} inputProps={{ min: 0, max: 26 }} sx={{ width: '60px' }} /></Grid>
                  <Grid item><Typography>Nakshatras apart</Typography></Grid>
                  <Grid item><Typography>assign value</Typography></Grid>
                  <Grid item><TextField size="small" placeholder="Value" type="number" value={condition4.value} onChange={e => setCondition4(p => ({ ...p, value: e.target.value }))} sx={{ width: '60px' }} /></Grid>
                </ConditionCard>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleAddComponent({ type: 4, ...condition4, nakshatraApart: String(Number(condition4.nakshatraApart) + 1), summary: `Condition 4: ${condition4.planet1} and ${condition4.planet2} ${condition4.nakshatraApart} Nakshatras apart` })} disabled={!areAllFieldsFilled(condition4, ['planet1', 'planet2', 'nakshatraApart', 'value']) || condition4.planet1 === condition4.planet2}>ADD</Button>
            </Box>


            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <ConditionCard>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition6.planet1} onChange={e => setCondition6(p => ({ ...p, planet1: e.target.value }))}>{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>and</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition6.planet2} onChange={e => setCondition6(p => ({ ...p, planet2: e.target.value }))}>{PLANETS.filter(p => p !== condition6.planet1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>in</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition6.nakshatra} onChange={e => setCondition6(p => ({ ...p, nakshatra: e.target.value }))}>{NAKSHATRAS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>assign value</Typography></Grid>
                  <Grid item><TextField size="small" placeholder="Value" type="number" value={condition6.value} onChange={e => setCondition6(p => ({ ...p, value: e.target.value }))} sx={{ width: '60px' }} /></Grid>
                </ConditionCard>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleAddComponent({ type: 6, ...condition6, summary: `Condition 6: ${condition6.planet1} and ${condition6.planet2} in ${condition6.nakshatra}` })} disabled={!areAllFieldsFilled(condition6, ['planet1', 'planet2', 'nakshatra', 'value']) || condition6.planet1 === condition6.planet2}>ADD</Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <ConditionCard>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition7.planet1} onChange={e => setCondition7(p => ({ ...p, planet1: e.target.value }))}>{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>and</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition7.planet2} onChange={e => setCondition7(p => ({ ...p, planet2: e.target.value }))}>{PLANETS.filter(p => p !== condition7.planet1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><TextField size="small" type="number" value={condition7.signsApart} onChange={e => { const v = e.target.value; if (v === '' || (Number(v) >= 0 && Number(v) <= 11)) setCondition7(p => ({ ...p, signsApart: v })); }} inputProps={{ min: 0, max: 11 }} sx={{ width: '60px' }} /></Grid>
                  <Grid item><Typography>signs apart</Typography></Grid>
                  <Grid item><Typography>assign value</Typography></Grid>
                  <Grid item><TextField size="small" placeholder="Value" type="number" value={condition7.value} onChange={e => setCondition7(p => ({ ...p, value: e.target.value }))} sx={{ width: '60px' }} /></Grid>
                </ConditionCard>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleAddComponent({ type: 7, ...condition7, signsApart: String(Number(condition7.signsApart) + 1), summary: `Condition 7: ${condition7.planet1} and ${condition7.planet2} ${condition7.signsApart} signs apart` })} disabled={!areAllFieldsFilled(condition7, ['planet1', 'planet2', 'signsApart', 'value']) || condition7.planet1 === condition7.planet2}>ADD</Button>
            </Box>


            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <ConditionCard>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition9.planet1} onChange={e => setCondition9(p => ({ ...p, planet1: e.target.value }))}>{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>in</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition9.sign} onChange={e => setCondition9(p => ({ ...p, sign: e.target.value }))}>{SIGNS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>assign value</Typography></Grid>
                  <Grid item><TextField size="small" placeholder="Value" type="number" value={condition9.value} onChange={e => setCondition9(p => ({ ...p, value: e.target.value }))} sx={{ width: '60px' }} /></Grid>
                </ConditionCard>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleAddComponent({ type: 9, ...condition9, summary: `Condition 9: ${condition9.planet1} in ${condition9.sign}` })} disabled={!areAllFieldsFilled(condition9, ['planet1', 'sign', 'value'])}>ADD</Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <ConditionCard>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition10.planet1} onChange={e => setCondition10(p => ({ ...p, planet1: e.target.value }))}>{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>in</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition10.nakshatra} onChange={e => setCondition10(p => ({ ...p, nakshatra: e.target.value }))}>{NAKSHATRAS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>assign value</Typography></Grid>
                  <Grid item><TextField size="small" placeholder="Value" type="number" value={condition10.value} onChange={e => setCondition10(p => ({ ...p, value: e.target.value }))} sx={{ width: '60px' }} /></Grid>
                </ConditionCard>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleAddComponent({ type: 10, ...condition10, summary: `Condition 10: ${condition10.planet1} in ${condition10.nakshatra}` })} disabled={!areAllFieldsFilled(condition10, ['planet1', 'nakshatra', 'value'])}>ADD</Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <ConditionCard>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition11.planet1} onChange={e => setCondition11(p => ({ ...p, planet1: e.target.value }))}>{PLANETS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>when</Typography></Grid>
                  <Grid item xs><FormControl size="small" fullWidth><Select value={condition11.state} onChange={e => setCondition11(p => ({ ...p, state: e.target.value }))}>{PLANET_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</Select></FormControl></Grid>
                  <Grid item><Typography>assign value</Typography></Grid>
                  <Grid item><TextField size="small" placeholder="Value" type="number" value={condition11.value} onChange={e => setCondition11(p => ({ ...p, value: e.target.value }))} sx={{ width: '60px' }} /></Grid>
                </ConditionCard>
              </Box>
              <Button variant="contained" size="small" onClick={() => handleAddComponent({ type: 11, ...condition11, summary: `Condition 11: ${condition11.planet1} when ${condition11.state}` })} disabled={!areAllFieldsFilled(condition11, ['planet1', 'state', 'value'])}>ADD</Button>
          </Box>

        </Box>

        {/* Right Panel: Execution Controls */}
        <Box sx={{ flex: 1, border: '1px solid #ccc', borderRadius: 1, p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <div>
            <Typography fontWeight="bold" mb={1}>Select Highlight Color</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={highlightColor}
                onChange={e => setHighlightColor(e.target.value)}
                renderValue={(selectedValue) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, backgroundColor: selectedValue, borderRadius: '4px', border: '1px solid #ccc' }} />
                    {COLOR_OPTIONS.find(c => c.value === selectedValue)?.name}
                  </Box>
                )}
                MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
              >
                {COLOR_OPTIONS.map((color) => (
                  <MenuItem key={color.name} value={color.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, backgroundColor: color.value, borderRadius: '4px', border: '1px solid #ccc' }} />
                      {color.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <Box mt={2}>
            <Typography variant="body2">Highlight Area when total strategy score is equal to or more than</Typography>
            <TextField
              size="small"
              type="number"
              fullWidth
              value={scoreThreshold}
              onChange={e => setScoreThreshold(e.target.value)}
            />
          </Box>
        </Box>
      </Box> {/* This closes the main flex container */}

        {/* Bottom Panel: Selected Components */}
        <Box mt={3}>
          <Typography fontWeight="bold">Selected Strategy Components</Typography>
          <List sx={{ border: '1px solid #ccc', borderRadius: 1, minHeight: 150, maxHeight: 200, overflow: 'auto' }}>
            {selectedComponents.length === 0 ? (
              <ListItem><ListItemText primary="No components added yet." /></ListItem>
            ) : (
              selectedComponents.map((component, index) => (
                <ListItem key={index}>
                  <ListItemText primary={component.summary} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleDeleteComponent(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))
            )}
          </List>
        </Box>

        {/* Action Buttons */}
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={onClose} variant="contained" color="error">Close</Button>
          <Button onClick={handleRun} variant="contained" disabled={selectedComponents.length === 0}>Run</Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

Strategy2DesignDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  /** A Command Bus prefill. Applied once per distinct object; null means "no prefill". */
  initialConfig: PropTypes.object,
};

export default Strategy2DesignDialog;
